import { z } from 'zod'
import type { RegimeContraparte } from './contraparte'

/**
 * Cadastro público do CNPJ (ADR 0032): o que a Receita diz da contraparte — natureza jurídica, opção
 * pelo Simples e pelo MEI. É o que permite responder, antes de perguntar a alguém, três coisas:
 *
 *   1. é do Simples? → então vira pergunta (puro ou híbrido em 2027);
 *   2. é condomínio, órgão público ou associação? → não aproveita crédito, e isso não muda;
 *   3. não é nenhum dos dois? → regime regular (Lucro Real ou Presumido), crédito integral.
 *
 * A natureza jurídica resolve o item 2 com precisão; o nome é o reforço para quando não há consulta.
 */

export const DadosCnpj = z.object({
  cnpj: z.string().min(14).max(14),
  razaoSocial: z.string().nullable(),
  nomeFantasia: z.string().nullable().optional(),
  /** código da tabela oficial de naturezas jurídicas, só dígitos (ex.: "2062", "3085") */
  naturezaCodigo: z.string().nullable(),
  naturezaNome: z.string().nullable(),
  /** optante pelo Simples Nacional na data da consulta */
  simples: z.boolean().nullable(),
  mei: z.boolean().nullable(),
  /** ATIVA, BAIXADA, SUSPENSA, INAPTA, NULA */
  situacao: z.string().nullable(),
  uf: z.string().nullable().optional(),
  municipio: z.string().nullable().optional(),
})
export type DadosCnpj = z.infer<typeof DadosCnpj>

/**
 * Como a contraparte se comporta no IBS/CBS. É o que o escritório quer ver na lista, e é mais direto
 * do que o regime: "institucional" responde sozinho que não há crédito a esperar dali.
 */
export const PERFIS_CONTRAPARTE = ['SIMPLES', 'MEI', 'REGULAR', 'INSTITUCIONAL', 'PESSOA_FISICA', 'EXTERIOR', 'INDEFINIDO'] as const
export type PerfilContraparte = (typeof PERFIS_CONTRAPARTE)[number]

export const ROTULO_PERFIL: Record<PerfilContraparte, string> = {
  SIMPLES: 'Simples Nacional',
  MEI: 'MEI',
  REGULAR: 'Regime regular (Real/Presumido)',
  INSTITUCIONAL: 'Condomínio, órgão público ou associação',
  PESSOA_FISICA: 'Pessoa física',
  EXTERIOR: 'Exterior',
  INDEFINIDO: 'A descobrir',
}

/**
 * Grupos da tabela oficial de naturezas jurídicas:
 *   1xxx — Administração Pública (união, estados, municípios, autarquias, fundações públicas)
 *   2xxx — Entidades Empresariais (LTDA, S.A., EIRELI, empresário individual…)
 *   3xxx — Entidades sem Fins Lucrativos (associação, fundação, condomínio edilício, sindicato, igreja)
 *   4xxx — Pessoas Físicas
 *   5xxx — Organizações Internacionais e outras instituições extraterritoriais
 *
 * Para o IBS/CBS o que importa é: 1 e 3 não são contribuintes habituais e não aproveitam crédito; 2 é
 * empresa (e aí a opção pelo Simples decide o resto); 5 é exterior.
 */
export function perfilPorNatureza(naturezaCodigo: string | null | undefined): PerfilContraparte | null {
  const c = (naturezaCodigo ?? '').replace(/\D/g, '')
  if (!c) return null
  switch (c[0]) {
    case '1':
    case '3':
      return 'INSTITUCIONAL'
    case '2':
      return 'REGULAR'
    case '4':
      return 'PESSOA_FISICA'
    case '5':
      return 'EXTERIOR'
    default:
      return null
  }
}

/** Sem acento e em maiúsculas, para casar o nome com os padrões abaixo. */
const semAcento = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()

/**
 * Reforço pelo nome, para quando não há consulta ou ela não trouxe a natureza. São padrões que no Brasil
 * só aparecem em quem não aproveita crédito; palavra que também cabe em empresa (como "instituto" em
 * "Instituto de Beleza") fica de fora de propósito — falso positivo aqui vira crédito perdido na conta.
 */
const PADROES_INSTITUCIONAIS: Array<[RegExp, string]> = [
  [/\bCONDOMINIO\b/, 'condomínio'],
  [/\bPREFEITURA\b/, 'prefeitura'],
  [/\bMUNICIPIO D[EO]\b/, 'município'],
  [/\bCAMARA (MUNICIPAL|DE VEREADORES)\b/, 'câmara municipal'],
  [/\bASSEMBLEIA LEGISLATIVA\b/, 'assembleia legislativa'],
  [/\bGOVERNO D[EOA]\b/, 'governo'],
  [/\bESTADO D[EO]\b/, 'estado'],
  [/\bSECRETARIA (DE|MUNICIPAL|ESTADUAL)\b/, 'secretaria'],
  [/\bMINISTERIO\b/, 'ministério'],
  [/\bTRIBUNAL\b/, 'tribunal'],
  [/\bDEFENSORIA PUBLICA\b/, 'defensoria pública'],
  [/\bAUTARQUIA\b/, 'autarquia'],
  [/\bFUNDO (MUNICIPAL|ESTADUAL|NACIONAL)\b/, 'fundo público'],
  [/\bASSOCIACAO\b/, 'associação'],
  [/\bFUNDACAO\b/, 'fundação'],
  [/\bSINDICATO\b/, 'sindicato'],
  [/\bCONSELHO (REGIONAL|FEDERAL)\b/, 'conselho profissional'],
  [/\b(IGREJA|PAROQUIA|MITRA|DIOCESE)\b/, 'organização religiosa'],
  [/\bSERVICO SOCIAL (AUTONOMO|DA INDUSTRIA|DO COMERCIO)\b/, 'serviço social autônomo'],
  [/\bCOOPERATIVA HABITACIONAL\b/, 'cooperativa habitacional'],
]

/** O nome sugere quem não aproveita crédito? Devolve o motivo, ou null. */
export function institucionalPeloNome(nome: string | null | undefined): string | null {
  if (!nome) return null
  const n = semAcento(nome)
  for (const [re, motivo] of PADROES_INSTITUCIONAIS) if (re.test(n)) return motivo
  return null
}

export interface SinaisContraparte {
  tipoPessoa: 'CNPJ' | 'CPF' | 'ESTRANGEIRO'
  nome: string | null
  /** o que a Receita respondeu, quando já houve consulta */
  cadastroCnpj?: DadosCnpj | null
  /** CRT das notas emitidas por ela */
  regimeDeclarado?: 'SIMPLES' | 'SIMPLES_EXCESSO' | 'NORMAL' | 'MEI' | null
  /** regime no cadastro do Processos, quando ela é da carteira do escritório */
  cadastroProcessos?: string | null
}

export interface TriagemContraparte {
  perfil: PerfilContraparte
  /** regime já no formato do cadastro — null quando não dá para afirmar */
  regime: RegimeContraparte | null
  motivo: string
  /** de onde veio a conclusão, da mais forte para a mais fraca */
  base: 'RECEITA' | 'CADASTRO_PROCESSOS' | 'DOCUMENTO' | 'NOME' | 'NENHUMA'
  /** Simples: falta saber se vai destacar IBS/CBS em 2027 — é o questionário */
  perguntarPuroOuHibrido: boolean
}

/**
 * A triagem completa, na ordem de força da evidência: o que a Receita diz vence o cadastro do escritório,
 * que vence o CRT das notas, que vence o nome.
 *
 * `padraoSn` só decide o palpite inicial de puro × híbrido; a pergunta continua valendo.
 */
export function triarContraparte(s: SinaisContraparte, padraoSn: 'SN_PURO' | 'SN_HIBRIDO' = 'SN_PURO'): TriagemContraparte {
  if (s.tipoPessoa === 'CPF') {
    return { perfil: 'PESSOA_FISICA', regime: 'PF_NAO_CONTRIBUINTE', motivo: 'pessoa física', base: 'DOCUMENTO', perguntarPuroOuHibrido: false }
  }
  if (s.tipoPessoa === 'ESTRANGEIRO') {
    return { perfil: 'EXTERIOR', regime: 'DESCONHECIDO', motivo: 'contraparte no exterior', base: 'DOCUMENTO', perguntarPuroOuHibrido: false }
  }

  const c = s.cadastroCnpj
  if (c) {
    const porNatureza = perfilPorNatureza(c.naturezaCodigo)
    if (porNatureza === 'INSTITUCIONAL') {
      return {
        perfil: 'INSTITUCIONAL',
        regime: 'IMUNE_ISENTO',
        motivo: `${c.naturezaNome ?? 'natureza jurídica de administração pública ou entidade sem fins lucrativos'} na Receita: não aproveita crédito`,
        base: 'RECEITA',
        perguntarPuroOuHibrido: false,
      }
    }
    if (c.mei) return { perfil: 'MEI', regime: 'MEI', motivo: 'MEI na Receita', base: 'RECEITA', perguntarPuroOuHibrido: false }
    if (c.simples === true) {
      return { perfil: 'SIMPLES', regime: padraoSn, motivo: 'optante pelo Simples Nacional na Receita', base: 'RECEITA', perguntarPuroOuHibrido: true }
    }
    if (c.simples === false && porNatureza === 'REGULAR') {
      return {
        perfil: 'REGULAR',
        regime: 'REGULAR',
        motivo: 'empresa não optante pelo Simples na Receita: Lucro Real ou Presumido, com crédito integral',
        base: 'RECEITA',
        perguntarPuroOuHibrido: false,
      }
    }
  }

  // Sem consulta (ou consulta incompleta): o cadastro do escritório é a melhor evidência seguinte.
  const cad = (s.cadastroProcessos ?? '').toLowerCase()
  if (cad === 'simples_nacional') {
    return { perfil: 'SIMPLES', regime: padraoSn, motivo: 'Simples no cadastro do Processos', base: 'CADASTRO_PROCESSOS', perguntarPuroOuHibrido: true }
  }
  if (cad === 'mei') return { perfil: 'MEI', regime: 'MEI', motivo: 'MEI no cadastro do Processos', base: 'CADASTRO_PROCESSOS', perguntarPuroOuHibrido: false }
  if (cad === 'lucro_presumido' || cad === 'lucro_real' || cad === 'lucro_arbitrado') {
    return { perfil: 'REGULAR', regime: 'REGULAR', motivo: `${cad.replace('_', ' ')} no cadastro do Processos`, base: 'CADASTRO_PROCESSOS', perguntarPuroOuHibrido: false }
  }

  switch (s.regimeDeclarado) {
    case 'MEI':
      return { perfil: 'MEI', regime: 'MEI', motivo: 'MEI declarado nas notas que ela emite', base: 'DOCUMENTO', perguntarPuroOuHibrido: false }
    case 'SIMPLES':
    case 'SIMPLES_EXCESSO':
      return { perfil: 'SIMPLES', regime: padraoSn, motivo: 'Simples declarado nas notas que ela emite', base: 'DOCUMENTO', perguntarPuroOuHibrido: true }
    case 'NORMAL':
      return { perfil: 'REGULAR', regime: 'REGULAR', motivo: 'regime normal declarado nas notas que ela emite', base: 'DOCUMENTO', perguntarPuroOuHibrido: false }
  }

  const peloNome = institucionalPeloNome(s.nome)
  if (peloNome) {
    return {
      perfil: 'INSTITUCIONAL',
      regime: 'IMUNE_ISENTO',
      motivo: `o nome indica ${peloNome}: não aproveita crédito — confirme antes de fechar a conta`,
      base: 'NOME',
      perguntarPuroOuHibrido: false,
    }
  }
  return { perfil: 'INDEFINIDO', regime: null, motivo: 'sem consulta à Receita, sem cadastro e sem nota emitida por ela', base: 'NENHUMA', perguntarPuroOuHibrido: false }
}
